import { supabase } from '../lib/supabase';
import {
  DiagnosisSession,
  DiagnosisReport,
  PrincipleQuestion,
  UserAnswer,
  DiagnosisHistoryItem,
  GetHistoryResponse,
  SystemType,
  SessionStatus,
  GenerateReportRequest,
  SaveSessionRequest
} from '../types/problemDiagnosis';
import { manageDiagnosisSessions, manageDiagnosisAnswers, manageDiagnosisTemplates } from '../lib/api';

export class ProblemDiagnosisService {

  /**
   * 生成UUID格式的字符串
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * 获取或创建匿名用户ID
   */
  private getAnonymousUserId(): string {
    // 尝试从localStorage获取现有的匿名用户ID
    let anonymousUserId = localStorage.getItem('anonymous_user_id');

    if (!anonymousUserId) {
      // 创建新的匿名用户ID（UUID格式）
      anonymousUserId = this.generateUUID();
      localStorage.setItem('anonymous_user_id', anonymousUserId);
      console.log('创建新的匿名用户ID:', anonymousUserId);
    }

    return anonymousUserId;
  }

  /**
   * 确保用户已登录，如果没有则使用匿名用户ID
   */
  async ensureUserLoggedIn(): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();

    if (user) {
      console.log('用户已登录:', user.id);
      return user.id;
    } else {
      console.log('用户未登录，使用匿名用户ID');
      return this.getAnonymousUserId();
    }
  }

  /**
   * 创建新的诊断会话
   */
  async createSession(problemDescription: string): Promise<DiagnosisSession> {
    const userId = await this.ensureUserLoggedIn();

    const response = await manageDiagnosisSessions('create', {
      user_id: userId,
      problem_description: problemDescription,
      current_step: 'input',
      status: 'active'
    });

    if (!response.success) {
      console.error('创建诊断会话失败:', response.error);
      throw new Error(response.error || '创建诊断会话失败');
    }

    console.log('诊断会话创建成功:', response.data);
    return response.data.session as DiagnosisSession;
  }

  /**
   * 更新诊断会话
   */
  async updateSession(sessionId: string, updates: Partial<DiagnosisSession>): Promise<DiagnosisSession> {
    const response = await manageDiagnosisSessions('update', {
      session_id: sessionId,
      ...updates,
      updated_at: new Date().toISOString()
    });

    if (!response.success) {
      console.error('更新诊断会话失败:', response.error);
      throw new Error(response.error || '更新诊断会话失败');
    }

    return response.data.session as DiagnosisSession;
  }

  /**
   * 获取指定系统的问题模板
   */
  async getQuestions(systemType: SystemType): Promise<PrincipleQuestion[]> {
    try {
      const response = await manageDiagnosisTemplates('list', {
        filters: { system_type: systemType, status: 'active' },
        fields: ['id', 'principle_type', 'question_text', 'order_index']
      });

      if (!response.success) {
        throw new Error(response.error || '获取问题模板失败');
      }

      return response.data || [];
    } catch (error) {
      console.error('获取问题模板失败:', error);
      throw new Error('获取问题模板失败');
    }
  }

  /**
   * 保存用户答案
   */
  async saveAnswers(sessionId: string, answers: UserAnswer[]): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('用户未登录');
    }

    // 先删除现有答案
    await manageDiagnosisAnswers('delete', {
      filters: { session_id: sessionId }
    });

    // 插入新答案
    const answersToInsert = answers.map(answer => ({
      session_id: sessionId,
      principle_type: answer.principle_type,
      question_text: answer.question_text,
      user_answer: answer.user_answer,
      answer_order: answer.answer_order
    }));

    const response = await manageDiagnosisAnswers('create', {
      batch_data: answersToInsert
    });

    if (!response.success) {
      console.error('保存答案失败:', response.error);
      throw new Error(response.error || '保存答案失败');
    }
  }

  /**
   * 使用 Edge Function 保存会话和答案
   */
  async saveSessionWithAnswers(request: SaveSessionRequest): Promise<DiagnosisSession> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/diagnosis-save-session`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token || supabase.supabaseKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request)
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || '保存会话失败');
      }

      return data.session;
    } catch (error) {
      console.error('保存会话失败:', error);
      throw new Error('保存会话失败');
    }
  }

  /**
   * 生成诊断报告
   */
  async generateReport(request: GenerateReportRequest): Promise<DiagnosisReport> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/diagnosis-generate-report`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token || supabase.supabaseKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request)
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || '生成诊断报告失败');
      }

      return data.report;
    } catch (error) {
      console.error('生成诊断报告失败:', error);
      throw new Error('生成诊断报告失败');
    }
  }

  /**
   * 获取用户的诊断历史
   */
  async getHistory(options: {
    page?: number;
    limit?: number;
    status?: SessionStatus;
    session_id?: string;
  } = {}): Promise<GetHistoryResponse> {
    const { page = 1, limit = 10, status, session_id } = options;
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      console.log('🔍 getHistory调试信息:');
      console.log('  - session存在:', !!session);
      console.log('  - session用户ID:', session?.user?.id);
      console.log('  - access_token存在:', !!session?.access_token);
      console.log('  - sessionError:', sessionError);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (status) {
        params.append('status', status);
      }

      if (session_id) {
        params.append('session_id', session_id);
      }

      // 处理认证token和匿名用户
      let authToken = session?.access_token;
      let headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
        console.log('  - 使用认证用户token');
      } else {
        // 匿名用户模式
        const anonymousUserId = this.getAnonymousUserId();
        params.append('anonymous_user_id', anonymousUserId);
        headers['Authorization'] = `Bearer ${supabase.supabaseKey}`;
        console.log('  - 使用匿名用户模式，ID:', anonymousUserId);
      }

      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/diagnosis-get-history?${params}`,
        {
          method: 'GET',
          headers,
        }
      );

      console.log('  - Edge Function响应状态:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('  - Edge Function错误响应:', errorText);
        throw new Error(`Edge Function调用失败: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('  - Edge Function响应数据:', data);

      if (!data.success) {
        throw new Error(data.error || '获取诊断历史失败');
      }

      return data;
    } catch (error) {
      console.error('获取诊断历史失败:', error);
      throw new Error('获取诊断历史失败');
    }
  }

  /**
   * 获取特定会话的详细信息
   */
  async getSessionDetail(sessionId: string): Promise<DiagnosisHistoryItem> {
    const { data, error } = await supabase.functions.invoke('diagnosis-get-history', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      body: new URLSearchParams({ session_id: sessionId })
    });

    if (error) {
      console.error('获取会话详情失败:', error);
      throw new Error('获取会话详情失败');
    }

    if (!data.success || !data.sessions || data.sessions.length === 0) {
      throw new Error('会话不存在');
    }

    return data.sessions[0];
  }

  /**
   * 删除诊断会话
   */
  async deleteSession(sessionId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('用户未登录');
    }

    const response = await manageDiagnosisSessions('delete', {
      id: sessionId
    });

    if (!response.success) {
      console.error('删除会话失败:', response.error);
      throw new Error(response.error || '删除会话失败');
    }
  }

  /**
   * 获取诊断统计信息
   */
  async getStatistics(): Promise<{
    total_sessions: number;
    completed_sessions: number;
    active_sessions: number;
    systems_used: Record<SystemType, number>;
  }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('用户未登录');
    }

    const response = await manageDiagnosisSessions('list', {
      action_type: 'statistics',
      user_id: user.id
    });

    if (!response.success) {
      throw new Error(response.error || '获取统计信息失败');
    }

    return response.data || {
      total_sessions: 0,
      completed_sessions: 0,
      active_sessions: 0,
      systems_used: {
        machine: 0,
        organism: 0,
        social: 0,
        ecosystem: 0
      }
    };
  }
}

// 导出单例实例
export const problemDiagnosisService = new ProblemDiagnosisService();
